import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Search, User, Briefcase, FileText, Clock, ArrowRight, FolderOpen, Archive, RotateCcw, AlertTriangle } from "lucide-react";
import { EmployeeDocuments } from "./DocumentsTab";
import { formatDateES } from "@/lib/dateUtils";

const STATUS_COLORS = {
  alta: "#22c55e", baja_medica: "#ef4444", vacaciones: "#3b82f6",
  excedencia: "#f59e0b", baja_definitiva: "#94a3b8",
};
const STATUS_LABELS = {
  alta: "Alta", baja_medica: "Baja medica", vacaciones: "Vacaciones",
  excedencia: "Excedencia", baja_definitiva: "Baja definitiva",
};
const EVENT_COLORS = {
  alta: "bg-green-500", cambio_centro: "bg-blue-500", cambio_departamento: "bg-indigo-500",
  cambio_puesto: "bg-purple-500", cambio_jefe: "bg-cyan-500", cambio_situacion: "bg-amber-500",
  cambio_contrato: "bg-orange-500", cambio_datos: "bg-slate-400",
};
const EVENT_LABELS = {
  alta: "Alta", cambio_centro: "Cambio de centro", cambio_departamento: "Cambio de departamento",
  cambio_puesto: "Cambio de puesto", cambio_jefe: "Cambio de jefe directo",
  cambio_situacion: "Cambio de situacion", cambio_contrato: "Cambio de contrato",
  cambio_datos: "Modificacion de datos",
};

const EMPTY_FORM = {
  name: "", first_name: "", last_name: "", dni_nie: "", birth_date: "",
  phone: "", personal_email: "", email: "", address: "", postal_code: "",
  city: "", province: "",
  center_id: "", department_id: "", position_id: "", direct_manager_id: "",
  professional_category_id: "", cotization_group_id: "", contract_type: "",
  workday_type: "", weekly_hours: 0, hire_date: "", seniority_date: "",
  leave_date: "", employment_status: "alta", agreement_id: "", cost_center_id: "",
  ss_number: "", internal_ref: "", contract_end_date: "",
  workday_percentage: 100, internal_notes: "",
};

function FormField({ label, children, span = 1, manageRoute, canManage }) {
  return (
    <div className={`space-y-1 ${span === 2 ? "col-span-2" : ""}`}>
      <div className="flex items-center justify-between gap-2">
        <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</Label>
        {manageRoute && canManage && (
          <a href={manageRoute} target="_blank" rel="noopener noreferrer"
             className="text-[9px] text-slate-400 hover:text-slate-800 underline" data-testid={`manage-${manageRoute.split('/').pop()}`}>
            Gestionar →
          </a>
        )}
      </div>
      {children}
    </div>
  );
}

function HistoryTimeline({ history }) {
  if (!history || history.length === 0) {
    return <p className="text-center text-xs text-slate-400 py-8">Sin historial registrado</p>;
  }
  return (
    <div className="space-y-0" data-testid="employee-history-timeline">
      {history.map((ev, i) => {
        const color = EVENT_COLORS[ev.event_type] || "bg-slate-400";
        const label = EVENT_LABELS[ev.event_type] || ev.event_type;
        const date = new Date(ev.created_at);
        const dateStr = (() => { const dd = String(date.getDate()).padStart(2, "0"); const mm = String(date.getMonth() + 1).padStart(2, "0"); const yyyy = date.getFullYear(); return `${dd}-${mm}-${yyyy}`; })();
        const timeStr = (() => { const hh = String(date.getHours()).padStart(2, "0"); const min = String(date.getMinutes()).padStart(2, "0"); return `${hh}:${min}`; })();
        return (
          <div key={ev.id} className="flex gap-3" data-testid={`history-event-${ev.id}`}>
            {/* Timeline line */}
            <div className="flex flex-col items-center w-5">
              <div className={`w-2.5 h-2.5 rounded-full ${color} flex-shrink-0 mt-1.5`} />
              {i < history.length - 1 && <div className="w-px flex-1 bg-slate-200" />}
            </div>
            {/* Content */}
            <div className="pb-4 flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-slate-800">{label}</span>
                <span className="text-[10px] text-slate-400 font-mono">{dateStr} {timeStr}</span>
              </div>
              <p className="text-xs text-slate-600 mt-0.5">{ev.description}</p>
              {ev.before?.display && ev.after?.display && (
                <div className="flex items-center gap-1.5 mt-1 text-[10px]">
                  <span className="text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded">{ev.before.display}</span>
                  <ArrowRight className="w-2.5 h-2.5 text-slate-300" />
                  <span className="text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded font-medium">{ev.after.display}</span>
                </div>
              )}
              <p className="text-[10px] text-slate-400 mt-0.5">por {ev.actor_name}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function EmployeesPage() {
  const { axiosAuth, user } = useAuth();
  const navigate = useNavigate();
  const canManageCatalogs = ["super_admin", "admin", "rrhh"].includes(user?.role || "");
  const [searchParams, setSearchParams] = useSearchParams();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterDept, setFilterDept] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterManager, setFilterManager] = useState("all");
  const [filterLink, setFilterLink] = useState("all");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [showArchived, setShowArchived] = useState(false);
  const [archiveDialog, setArchiveDialog] = useState(null); // {emp, deps}
  const [archiving, setArchiving] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");
  const [deactivateUserOnArchive, setDeactivateUserOnArchive] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [centers, setCenters] = useState([]);
  const [positions, setPositions] = useState([]);
  const [profCategories, setProfCategories] = useState([]);
  const [contribGroups, setContribGroups] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [constants, setConstants] = useState({});
  const [formTab, setFormTab] = useState("personal");
  const [provinces, setProvinces] = useState([]);
  const [municipalities, setMunicipalities] = useState([]);
  const [history, setHistory] = useState([]);

  const loadCatalogs = useCallback(async () => {
    try {
      const [dRes, cRes, pRes, constRes, pcRes, cgRes, agrRes, ccRes, provRes, ctRes, wdRes, esRes] = await Promise.all([
        axiosAuth().get("/departments"),
        axiosAuth().get("/centers"),
        axiosAuth().get("/positions"),
        axiosAuth().get("/catalogs/constants"),
        axiosAuth().get("/professional-categories"),
        axiosAuth().get("/contribution-groups"),
        axiosAuth().get("/agreements"),
        axiosAuth().get("/cost-centers"),
        axiosAuth().get("/catalogs/provinces"),
        axiosAuth().get("/hr-catalogs/contract_types?active_only=true"),
        axiosAuth().get("/hr-catalogs/workday_types?active_only=true"),
        axiosAuth().get("/hr-catalogs/employment_statuses?active_only=true"),
      ]);
      setDepartments(dRes.data);
      setCenters(cRes.data);
      setPositions(pRes.data);
      // Override constants with BD-driven catalogs (active only) so old + inactive don't show
      setConstants({
        ...constRes.data,
        contract_types: ctRes.data.map(x => ({ value: x.slug, label: x.name, ...x })),
        workday_types: wdRes.data.map(x => ({ value: x.slug, label: x.name, ...x })),
        employment_statuses: esRes.data.map(x => ({ value: x.slug, label: x.name, ...x })),
      });
      setProfCategories(pcRes.data);
      setContribGroups(cgRes.data);
      setAgreements(agrRes.data);
      setCostCenters(ccRes.data);
      setProvinces(provRes.data);
    } catch { /* ignore */ }
  }, [axiosAuth]);

  const loadEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const params = { include_archived: showArchived || filterLink === "archived" || filterLink === "inconsistencies" };
      if (filterLink && filterLink !== "all") params.link_filter = filterLink;
      // Filtros accionables desde Catalogos RRHH / Salud de Datos
      const missing = searchParams.get("missing");
      const usingInactive = searchParams.get("using_inactive");
      const usingArchived = searchParams.get("using_archived");
      const orphan = searchParams.get("orphan_value");
      if (missing) params.missing = missing;
      if (usingInactive) params.using_inactive = usingInactive;
      if (usingArchived) params.using_archived = usingArchived;
      if (orphan) params.orphan_value = orphan;
      const res = await axiosAuth().get("/employees", { params });
      setEmployees(res.data);
    } catch { toast.error("Error al cargar empleados"); }
    finally { setLoading(false); }
  }, [axiosAuth, showArchived, filterLink, searchParams]);

  useEffect(() => { loadCatalogs(); }, [loadCatalogs]);
  useEffect(() => { loadEmployees(); }, [loadEmployees]);

  // Open employee from URL query param (used by OrgChart navigation)
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (editId && employees.length > 0 && !editing) {
      const emp = employees.find(e => e.id === editId);
      if (emp) {
        openEdit(emp);
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, employees, editing]);

  const loadMunicipalities = useCallback(async (provinceName) => {
    if (!provinceName) { setMunicipalities([]); return; }
    const prov = provinces.find(p => p.name === provinceName);
    if (!prov) { setMunicipalities([]); return; }
    try {
      const res = await axiosAuth().get(`/catalogs/provinces/${prov.code}/municipalities`);
      setMunicipalities(res.data);
    } catch { setMunicipalities([]); }
  }, [axiosAuth, provinces]);

  useEffect(() => {
    if (form.province && provinces.length > 0) loadMunicipalities(form.province);
  }, [form.province, provinces.length, loadMunicipalities]);

  const loadHistory = useCallback(async (empId) => {
    try {
      const res = await axiosAuth().get(`/employees/${empId}/history`);
      setHistory(res.data);
    } catch { setHistory([]); }
  }, [axiosAuth]);

  const deptMap = {};
  departments.forEach(d => { deptMap[d.id] = d; deptMap[d.slug] = d; });
  const centerMap = {};
  centers.forEach(c => { centerMap[c.id] = c; centerMap[c.slug] = c; });
  const posMap = {};
  positions.forEach(p => { posMap[p.id] = p; });

  // Unique managers for filter
  const managersInUse = [...new Set(employees.filter(e => e.direct_manager_id).map(e => e.direct_manager_id))];

  const filtered = employees.filter(e => {
    if (search) {
      const s = search.toLowerCase();
      if (!((e.name || "").toLowerCase().includes(s) || (e.email || "").toLowerCase().includes(s) || (e.dni_nie || "").toLowerCase().includes(s))) return false;
    }
    if (filterDept !== "all" && e.department_id !== filterDept && e.department !== filterDept) return false;
    if (filterStatus !== "all" && (e.employment_status || "alta") !== filterStatus) return false;
    if (filterManager !== "all") {
      if (filterManager === "_none" && e.direct_manager_id) return false;
      if (filterManager !== "_none" && e.direct_manager_id !== filterManager) return false;
    }
    return true;
  });

  const openNew = () => {
    setEditing("new"); setForm({ ...EMPTY_FORM }); setFormTab("personal");
    setMunicipalities([]); setHistory([]);
  };

  const openEdit = (emp) => {
    setEditing(emp.id);
    const f = { ...EMPTY_FORM };
    Object.keys(f).forEach(k => { if (emp[k] !== undefined && emp[k] !== null) f[k] = emp[k]; });
    setForm(f); setFormTab("personal");
    loadHistory(emp.id);
  };

  const save = async () => {
    if (!form.first_name && !form.name) { toast.error("El nombre es obligatorio"); return; }
    const payload = { ...form };
    // Auto-generate name before sending
    if (payload.first_name || payload.last_name) {
      payload.name = `${payload.first_name} ${payload.last_name}`.trim();
    }
    if (!payload.email && editing === "new") {
      toast.error("El email corporativo es obligatorio para crear usuario"); return;
    }
    try {
      if (editing === "new") {
        await axiosAuth().post("/employees", payload);
        toast.success("Empleado creado");
      } else {
        await axiosAuth().put(`/employees/${editing}`, payload);
        toast.success("Empleado actualizado");
      }
      setEditing(null);
      loadEmployees();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
  };

  const f = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const autoName = form.first_name || form.last_name ? `${form.first_name} ${form.last_name}`.trim() : form.name;
  const inputCls = "h-8 text-xs rounded-sm";

  // Archive / Restore
  const prepareArchive = async (emp) => {
    try {
      const res = await axiosAuth().get(`/employees/${emp.id}/dependencies`);
      setArchiveDialog({ emp, deps: res.data });
    } catch { toast.error("Error al verificar dependencias"); }
  };

  const confirmArchive = async () => {
    if (!archiveDialog) return;
    if (!archiveReason.trim()) { toast.error("Debes indicar un motivo para archivar al empleado"); return; }
    setArchiving(true);
    try {
      await axiosAuth().delete(`/employees/${archiveDialog.emp.id}`, {
        data: { reason: archiveReason.trim(), deactivate_user: deactivateUserOnArchive }
      });
      if (archiveDialog.deps?.has_user && !deactivateUserOnArchive) {
        toast.warning(`Empleado archivado. ATENCIÓN: el usuario sigue activo (auditado).`);
      } else {
        toast.success(`Empleado '${archiveDialog.emp.name}' archivado`);
      }
      setArchiveDialog(null);
      setArchiveReason("");
      setDeactivateUserOnArchive(true);
      setEditing(null);
      loadEmployees();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al archivar"); }
    finally { setArchiving(false); }
  };

  const restoreEmployee = async (empId) => {
    try {
      await axiosAuth().put(`/employees/${empId}/restore`);
      toast.success("Empleado restaurado");
      loadEmployees();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al restaurar"); }
  };

  // Compute incidence-filter banner state
  const FIELD_LABELS_ES = {
    department_id: "departamento", center_id: "centro", position_id: "puesto",
    contract_type: "tipo de contrato", workday_type: "jornada",
    employment_status: "situacion laboral", professional_category_id: "categoria profesional",
    cotization_group_id: "grupo de cotizacion", agreement_id: "convenio",
    cost_center_id: "centro de coste", direct_manager_id: "jefe directo",
  };
  const missingFilter = searchParams.get("missing");
  const usingInactiveFilter = searchParams.get("using_inactive");
  const usingArchivedFilter = searchParams.get("using_archived");
  const orphanFilter = searchParams.get("orphan_value");
  const hasIncidenceFilter = !!(missingFilter || usingInactiveFilter || usingArchivedFilter || orphanFilter);

  const clearIncidenceFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("missing"); next.delete("using_inactive"); next.delete("using_archived"); next.delete("orphan_value");
    setSearchParams(next);
  };

  const incidenceBannerLabel = (() => {
    if (missingFilter) return `Mostrando empleados sin ${FIELD_LABELS_ES[missingFilter] || missingFilter} asignado`;
    if (usingInactiveFilter) return `Mostrando empleados con ${FIELD_LABELS_ES[usingInactiveFilter] || usingInactiveFilter} inactivo`;
    if (usingArchivedFilter) return `Mostrando empleados con ${FIELD_LABELS_ES[usingArchivedFilter] || usingArchivedFilter} archivado`;
    if (orphanFilter) {
      const [f, v] = orphanFilter.split(":");
      return `Mostrando empleados con valor huérfano: ${FIELD_LABELS_ES[f] || f} = "${v}"`;
    }
    return "";
  })();

  return (
    <div className="space-y-5" data-testid="employees-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="heading-industrial text-2xl font-bold text-slate-900 uppercase" data-testid="employees-title">Empleados</h1>
          <p className="text-sm text-slate-500">Gestion de personal, departamentos y fichas</p>
        </div>
        {["admin", "super_admin"].includes(user?.role) && (
          <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white text-xs rounded-sm" onClick={openNew} data-testid="btn-new-employee">
            <Plus className="w-3 h-3 mr-1" /> Nuevo Empleado
          </Button>
        )}
      </div>

      {/* Incidence-filter banner */}
      {hasIncidenceFilter && (
        <div className="flex items-center justify-between gap-3 bg-amber-50 border border-amber-300 rounded-sm px-3 py-2" data-testid="incidence-banner">
          <div className="flex items-center gap-2 min-w-0">
            <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0" />
            <span className="text-xs font-semibold text-amber-900 truncate" data-testid="incidence-banner-label">{incidenceBannerLabel}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={clearIncidenceFilter} className="h-7 text-xs rounded-sm" data-testid="clear-incidence-filter">
              Limpiar filtro
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/catalogos-rrhh")} className="h-7 text-xs rounded-sm" data-testid="back-to-catalogs">
              Volver a Catálogos RRHH
            </Button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-end">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nombre, email, DNI..." className="pl-7 h-8 text-xs rounded-sm" data-testid="employee-search" />
        </div>
        <Select value={filterDept} onValueChange={setFilterDept}>
          <SelectTrigger className="h-8 text-xs rounded-sm w-40" data-testid="filter-department">
            <SelectValue placeholder="Departamento" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los dept.</SelectItem>
            {departments.filter(d => d.active).map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="h-8 text-xs rounded-sm w-36" data-testid="filter-status">
            <SelectValue placeholder="Situacion" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            {(constants.employment_statuses || []).map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterManager} onValueChange={setFilterManager}>
          <SelectTrigger className="h-8 text-xs rounded-sm w-44" data-testid="filter-manager">
            <SelectValue placeholder="Jefe directo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los jefes</SelectItem>
            <SelectItem value="_none">Sin jefe asignado</SelectItem>
            {managersInUse.map(mId => {
              const mgr = employees.find(e => e.id === mId);
              return mgr ? <SelectItem key={mId} value={mId}>{mgr.name}</SelectItem> : null;
            })}
          </SelectContent>
        </Select>
        <Select value={filterLink} onValueChange={setFilterLink}>
          <SelectTrigger className="h-8 text-xs rounded-sm w-44" data-testid="filter-link-status">
            <SelectValue placeholder="Vinculacion" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="with_user">Con usuario</SelectItem>
            <SelectItem value="no_user">Sin usuario</SelectItem>
            <SelectItem value="user_disabled">Usuario desactivado</SelectItem>
            <SelectItem value="archived">Archivados</SelectItem>
            <SelectItem value="inconsistencies">Inconsistencias</SelectItem>
          </SelectContent>
        </Select>
        <Badge variant="outline" className="text-[10px] rounded-sm h-8 flex items-center">{filtered.length} empleados</Badge>
      </div>

      {/* Table */}
      <Card className="border-slate-200 rounded-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-[10px] uppercase">Empleado</TableHead>
                  <TableHead className="text-[10px] uppercase">Departamento</TableHead>
                  <TableHead className="text-[10px] uppercase">Centro</TableHead>
                  <TableHead className="text-[10px] uppercase">Puesto</TableHead>
                  <TableHead className="text-[10px] uppercase">Jefe</TableHead>
                  <TableHead className="text-[10px] uppercase">Situacion</TableHead>
                  <TableHead className="text-[10px] uppercase">Acceso</TableHead>
                  <TableHead className="text-[10px] uppercase">Alta</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400 text-xs">Cargando...</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400 text-xs">Sin empleados</TableCell></TableRow>
                ) : filtered.map(e => {
                  const dept = deptMap[e.department_id] || deptMap[e.department];
                  const center = centerMap[e.center_id] || centerMap[e.center];
                  const pos = posMap[e.position_id];
                  const es = e.employment_status || "alta";
                  return (
                    <TableRow key={e.id} className={`cursor-pointer hover:bg-slate-50 ${e.archived ? "opacity-50 bg-slate-50" : ""}`} onClick={() => openEdit(e)} data-testid={`employee-row-${e.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2.5">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${e.archived ? "bg-slate-300 text-slate-500" : "bg-slate-200 text-slate-600"}`}>{(e.name || "?").charAt(0).toUpperCase()}</div>
                          <div>
                            <p className="text-xs font-semibold flex items-center gap-1">
                              {e.name}
                              {e.archived && <Badge className="bg-slate-400 text-white text-[7px] px-1 py-0 rounded">ARCHIVADO</Badge>}
                            </p>
                            <p className="text-[10px] text-slate-400">{e.email || e.personal_email || "-"}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">{dept?.name || e.department || "-"}</TableCell>
                      <TableCell className="text-xs">{center?.name || e.center || "-"}</TableCell>
                      <TableCell className="text-xs">{pos?.name || e.position || "-"}</TableCell>
                      <TableCell className="text-xs">{e.direct_manager_name || "-"}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[9px]" style={{ color: STATUS_COLORS[es], borderColor: STATUS_COLORS[es] }}>
                          {STATUS_LABELS[es] || es}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {e.link_status === "linked" && <Badge className="bg-green-100 text-green-700 text-[8px] rounded-sm" data-testid={`link-badge-${e.id}`}>Vinculado</Badge>}
                        {e.link_status === "no_user" && <Badge className="bg-red-100 text-red-600 text-[8px] rounded-sm" data-testid={`link-badge-${e.id}`}>Sin usuario</Badge>}
                        {e.link_status === "user_disabled" && <Badge className="bg-amber-100 text-amber-700 text-[8px] rounded-sm" data-testid={`link-badge-${e.id}`}>Desactivado</Badge>}
                        {e.link_status === "archived" && <Badge className="bg-slate-200 text-slate-500 text-[8px] rounded-sm" data-testid={`link-badge-${e.id}`}>Archivado</Badge>}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] font-mono text-slate-500">{formatDateES(e.hire_date)}</span>
                          {e.archived && user?.role === "super_admin" && (
                            <Button variant="ghost" size="icon" className="h-6 w-6"
                              onClick={ev => { ev.stopPropagation(); restoreEmployee(e.id); }}
                              title="Restaurar empleado" data-testid={`btn-restore-${e.id}`}>
                              <RotateCcw className="w-3 h-3 text-blue-500" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Employee Form Dialog */}
      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="employee-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm uppercase tracking-wider">
              {editing === "new" ? "Nuevo Empleado" : `Editar: ${autoName || ""}`}
            </DialogTitle>
          </DialogHeader>

          <Tabs value={formTab} onValueChange={setFormTab}>
            {/* Archive banner for archived employees */}
            {editing !== "new" && employees.find(e => e.id === editing)?.archived && (() => {
              const emp = employees.find(e => e.id === editing);
              return (
                <Card className="border-red-200 bg-red-50/50 rounded-sm mb-3" data-testid="archive-info-banner">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Archive className="w-3.5 h-3.5 text-red-500" />
                      <span className="text-[10px] uppercase font-bold text-red-600 tracking-wider">Empleado Archivado</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-[11px]">
                      <div><span className="text-slate-500">Archivado por:</span> <span className="font-medium">{emp.archived_by || "-"}</span></div>
                      <div><span className="text-slate-500">Fecha:</span> <span className="font-mono">{emp.archived_at ? formatDateES(emp.archived_at) : "-"}</span></div>
                      <div><span className="text-slate-500">Motivo:</span> <span className="font-medium">{emp.archive_reason || "Sin motivo registrado"}</span></div>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}
            <TabsList className="bg-slate-100 rounded-sm h-8" data-testid="employee-form-tabs">
              <TabsTrigger value="personal" className="text-[10px] uppercase tracking-wider rounded-sm data-[state=active]:bg-white"><User className="w-3 h-3 mr-1" /> Personal</TabsTrigger>
              <TabsTrigger value="laboral" className="text-[10px] uppercase tracking-wider rounded-sm data-[state=active]:bg-white"><Briefcase className="w-3 h-3 mr-1" /> Laboral</TabsTrigger>
              <TabsTrigger value="admin" className="text-[10px] uppercase tracking-wider rounded-sm data-[state=active]:bg-white"><FileText className="w-3 h-3 mr-1" /> Administrativo</TabsTrigger>
              {editing !== "new" && (
                <TabsTrigger value="historial" className="text-[10px] uppercase tracking-wider rounded-sm data-[state=active]:bg-white"><Clock className="w-3 h-3 mr-1" /> Historial</TabsTrigger>
              )}
              {editing !== "new" && (
                <TabsTrigger value="documentos" className="text-[10px] uppercase tracking-wider rounded-sm data-[state=active]:bg-white"><FolderOpen className="w-3 h-3 mr-1" /> Documentos</TabsTrigger>
              )}
            </TabsList>

            {/* Personal Tab */}
            <TabsContent value="personal" className="mt-4">
              <div className="grid grid-cols-3 gap-3">
                <FormField label="Nombre *">
                  <Input value={form.first_name} onChange={e => f("first_name", e.target.value)} className={inputCls} data-testid="emp-first-name" placeholder="Nombre" />
                </FormField>
                <FormField label="Apellidos *">
                  <Input value={form.last_name} onChange={e => f("last_name", e.target.value)} className={inputCls} data-testid="emp-last-name" placeholder="Apellidos" />
                </FormField>
                <FormField label="Nombre completo (auto)">
                  <Input value={autoName} readOnly className={`${inputCls} bg-slate-50 text-slate-500`} data-testid="emp-name" />
                </FormField>
                <FormField label="DNI / NIE">
                  <Input value={form.dni_nie} onChange={e => f("dni_nie", e.target.value)} className={inputCls} data-testid="emp-dni" />
                </FormField>
                <FormField label="Fecha de nacimiento">
                  <Input type="date" value={form.birth_date} onChange={e => f("birth_date", e.target.value)} className={inputCls} />
                </FormField>
                <FormField label="Telefono">
                  <Input value={form.phone} onChange={e => f("phone", e.target.value)} className={inputCls} data-testid="emp-phone" />
                </FormField>
                <FormField label="Email corporativo *">
                  <Input type="email" value={form.email} onChange={e => f("email", e.target.value)} className={inputCls} data-testid="emp-email" />
                </FormField>
                <FormField label="Email personal">
                  <Input type="email" value={form.personal_email} onChange={e => f("personal_email", e.target.value)} className={inputCls} />
                </FormField>
                <div />
                <FormField label="Direccion" span={2}>
                  <Input value={form.address} onChange={e => f("address", e.target.value)} className={inputCls} />
                </FormField>
                <FormField label="Codigo postal">
                  <Input value={form.postal_code} onChange={e => f("postal_code", e.target.value)} className={inputCls} />
                </FormField>
                <FormField label="Provincia">
                  <Select value={form.province || "_none"} onValueChange={v => { f("province", v === "_none" ? "" : v); f("city", ""); }}>
                    <SelectTrigger className={inputCls} data-testid="emp-province"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      <SelectItem value="_none">- Sin provincia -</SelectItem>
                      {provinces.map(p => <SelectItem key={p.code} value={p.name}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Municipio">
                  <Select value={form.city || "_none"} onValueChange={v => f("city", v === "_none" ? "" : v)} disabled={!form.province}>
                    <SelectTrigger className={inputCls} data-testid="emp-city"><SelectValue placeholder={form.province ? "Seleccionar..." : "Elige provincia primero"} /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      <SelectItem value="_none">- Sin municipio -</SelectItem>
                      {municipalities.map(m => <SelectItem key={m.name} value={m.name}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormField>
              </div>
            </TabsContent>

            {/* Laboral Tab */}
            <TabsContent value="laboral" className="mt-4">
              <div className="grid grid-cols-3 gap-3">
                <FormField label="Centro" manageRoute="/admin/centros" canManage={user?.role === "super_admin" || user?.role === "admin"}>
                  <Select value={form.center_id || "_none"} onValueChange={v => f("center_id", v === "_none" ? "" : v)}>
                    <SelectTrigger className={inputCls} data-testid="emp-center"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">- Sin centro -</SelectItem>
                      {centers.filter(c => c.active).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Departamento" manageRoute="/admin/catalogos-rrhh/departamentos" canManage={canManageCatalogs}>
                  <Select value={form.department_id || "_none"} onValueChange={v => f("department_id", v === "_none" ? "" : v)}>
                    <SelectTrigger className={inputCls} data-testid="emp-department"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">- Sin departamento -</SelectItem>
                      {departments.filter(d => d.active).map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Puesto" manageRoute="/admin/catalogos-rrhh/puestos" canManage={canManageCatalogs}>
                  <Select value={form.position_id || "_none"} onValueChange={v => f("position_id", v === "_none" ? "" : v)}>
                    <SelectTrigger className={inputCls} data-testid="emp-position"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">- Sin puesto -</SelectItem>
                      {positions.filter(p => p.active).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Jefe directo">
                  <Select value={form.direct_manager_id || "_none"} onValueChange={v => f("direct_manager_id", v === "_none" ? "" : v)}>
                    <SelectTrigger className={inputCls} data-testid="emp-manager"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      <SelectItem value="_none">- Sin jefe -</SelectItem>
                      {employees.filter(e => e.id !== editing).map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Categoria profesional" manageRoute="/admin/catalogos-rrhh/categorias-profesionales" canManage={canManageCatalogs}>
                  <Select value={form.professional_category_id || "_none"} onValueChange={v => f("professional_category_id", v === "_none" ? "" : v)}>
                    <SelectTrigger className={inputCls} data-testid="emp-prof-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">- Sin categoria -</SelectItem>
                      {profCategories.filter(c => c.active).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Grupo de cotizacion" manageRoute="/admin/catalogos-rrhh/grupos-cotizacion" canManage={canManageCatalogs}>
                  <Select value={form.cotization_group_id || "_none"} onValueChange={v => f("cotization_group_id", v === "_none" ? "" : v)}>
                    <SelectTrigger className={inputCls} data-testid="emp-cotiz-group"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">- Sin grupo -</SelectItem>
                      {contribGroups.filter(g => g.active).map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Tipo de contrato" manageRoute="/admin/catalogos-rrhh/tipos-contrato" canManage={canManageCatalogs}>
                  <Select value={form.contract_type || "_none"} onValueChange={v => f("contract_type", v === "_none" ? "" : v)}>
                    <SelectTrigger className={inputCls} data-testid="emp-contract"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">- Sin tipo -</SelectItem>
                      {(constants.contract_types || []).map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Jornada" manageRoute="/admin/catalogos-rrhh/jornadas" canManage={canManageCatalogs}>
                  <Select value={form.workday_type || "_none"} onValueChange={v => f("workday_type", v === "_none" ? "" : v)}>
                    <SelectTrigger className={inputCls} data-testid="emp-workday"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">- Sin definir -</SelectItem>
                      {(constants.workday_types || []).map(w => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Horas semanales">
                  <Input type="number" value={form.weekly_hours} onChange={e => f("weekly_hours", parseFloat(e.target.value) || 0)} className={inputCls} />
                </FormField>
                <FormField label="Situacion laboral" manageRoute="/admin/catalogos-rrhh/situaciones-laborales" canManage={canManageCatalogs}>
                  <Select value={form.employment_status || "alta"} onValueChange={v => f("employment_status", v)}>
                    <SelectTrigger className={inputCls} data-testid="emp-employment-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(constants.employment_statuses || []).map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Fecha de alta">
                  <Input type="date" value={form.hire_date} onChange={e => f("hire_date", e.target.value)} className={inputCls} data-testid="emp-hire-date" />
                </FormField>
                <FormField label="Fecha de antiguedad">
                  <Input type="date" value={form.seniority_date} onChange={e => f("seniority_date", e.target.value)} className={inputCls} />
                </FormField>
                <FormField label="Fecha de baja">
                  <Input type="date" value={form.leave_date} onChange={e => f("leave_date", e.target.value)} className={inputCls} />
                </FormField>
                <FormField label="Convenio" manageRoute="/admin/catalogos-rrhh/convenios" canManage={canManageCatalogs}>
                  <Select value={form.agreement_id || "_none"} onValueChange={v => f("agreement_id", v === "_none" ? "" : v)}>
                    <SelectTrigger className={inputCls} data-testid="emp-agreement"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">- Sin convenio -</SelectItem>
                      {agreements.filter(a => a.active).map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Centro de coste" manageRoute="/admin/catalogos-rrhh/centros-coste" canManage={canManageCatalogs}>
                  <Select value={form.cost_center_id || "_none"} onValueChange={v => f("cost_center_id", v === "_none" ? "" : v)}>
                    <SelectTrigger className={inputCls} data-testid="emp-cost-center"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">- Sin centro de coste -</SelectItem>
                      {costCenters.filter(c => c.active).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormField>
              </div>
            </TabsContent>

            {/* Admin Tab */}
            <TabsContent value="admin" className="mt-4">
              <div className="grid grid-cols-3 gap-3">
                <FormField label="N SS (Seguridad Social)">
                  <Input value={form.ss_number} onChange={e => f("ss_number", e.target.value)} className={inputCls} data-testid="emp-ss" />
                </FormField>
                <FormField label="Referencia interna">
                  <Input value={form.internal_ref} onChange={e => f("internal_ref", e.target.value)} className={inputCls} />
                </FormField>
                <FormField label="Fecha fin contrato">
                  <Input type="date" value={form.contract_end_date} onChange={e => f("contract_end_date", e.target.value)} className={inputCls} />
                </FormField>
                <FormField label="% Jornada">
                  <Input type="number" value={form.workday_percentage} onChange={e => f("workday_percentage", parseFloat(e.target.value) || 100)} className={inputCls} />
                </FormField>
                <FormField label="Observaciones internas" span={2}>
                  <Textarea value={form.internal_notes} onChange={e => f("internal_notes", e.target.value)} className="text-xs rounded-sm h-20" data-testid="emp-notes" />
                </FormField>
              </div>
            </TabsContent>

            {/* Historial Tab */}
            {editing !== "new" && (
              <TabsContent value="historial" className="mt-4">
                <HistoryTimeline history={history} />
              </TabsContent>
            )}

            {/* Documentos Tab */}
            {editing !== "new" && (
              <TabsContent value="documentos" className="mt-4">
                <EmployeeDocuments axiosAuth={axiosAuth} employeeId={editing} employeeName={autoName} />
              </TabsContent>
            )}
          </Tabs>

          <DialogFooter className="mt-4">
            {editing !== "new" && user?.role === "super_admin" && !employees.find(e => e.id === editing)?.archived && (
              <Button variant="outline" size="sm" className="text-xs rounded-sm text-red-600 border-red-200 hover:bg-red-50 mr-auto"
                onClick={() => { const emp = employees.find(e => e.id === editing); if (emp) prepareArchive(emp); }}
                data-testid="btn-archive-employee">
                <Archive className="w-3 h-3 mr-1" /> Archivar empleado
              </Button>
            )}
            <Button variant="outline" size="sm" className="text-xs rounded-sm" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white text-xs rounded-sm" onClick={save} data-testid="btn-save-employee">
              {editing === "new" ? "Crear Empleado" : "Guardar Cambios"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog open={!!archiveDialog} onOpenChange={() => { setArchiveDialog(null); setArchiveReason(""); }}>
        <DialogContent className="max-w-md" data-testid="archive-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm uppercase tracking-wider text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Archivar Empleado
            </DialogTitle>
          </DialogHeader>
          {archiveDialog && (
            <div className="space-y-3">
              <p className="text-xs text-slate-700">
                Vas a archivar a <span className="font-bold">{archiveDialog.emp.name}</span>. Esta accion:
              </p>
              <ul className="text-[11px] text-slate-600 space-y-1 list-disc ml-4">
                <li>Marcara al empleado como <span className="font-semibold text-red-600">archivado</span></li>
                <li>No aparecera en listados normales</li>
                <li>No podra asignarse a nuevos flujos</li>
                {archiveDialog.deps.subordinates_count > 0 && <li>Sus <span className="font-semibold">{archiveDialog.deps.subordinates_count} subordinados</span> quedaran sin jefe directo</li>}
                <li>El historico, documentos y solicitudes se <span className="font-semibold text-green-600">mantienen intactos</span></li>
              </ul>

              {archiveDialog.deps.has_user && (
                <Card className={`border-2 rounded-sm ${deactivateUserOnArchive ? "border-amber-300 bg-amber-50" : "border-red-300 bg-red-50"}`} data-testid="user-deactivation-prompt">
                  <CardContent className="p-3">
                    <p className="text-xs font-semibold text-slate-800 mb-2">
                      Esta persona tiene un usuario activo en el panel. ¿Quieres desactivar también su acceso?
                    </p>
                    <div className="space-y-2">
                      <label className={`flex items-start gap-2 p-2 rounded-sm cursor-pointer border ${deactivateUserOnArchive ? "border-amber-400 bg-white" : "border-slate-200 bg-white/60"}`}>
                        <input type="radio" checked={deactivateUserOnArchive} onChange={() => setDeactivateUserOnArchive(true)}
                          className="mt-0.5" data-testid="opt-deactivate-user" />
                        <div className="text-xs">
                          <p className="font-semibold text-slate-800">Sí, desactivar también el usuario <span className="text-[9px] uppercase font-bold text-amber-700 ml-1">Recomendado</span></p>
                          <p className="text-slate-500 text-[10px]">Revoca el acceso al panel inmediatamente. La ficha NO se borra.</p>
                        </div>
                      </label>
                      <label className={`flex items-start gap-2 p-2 rounded-sm cursor-pointer border ${!deactivateUserOnArchive ? "border-red-400 bg-white" : "border-slate-200 bg-white/60"}`}>
                        <input type="radio" checked={!deactivateUserOnArchive} onChange={() => setDeactivateUserOnArchive(false)}
                          className="mt-0.5" data-testid="opt-keep-user" />
                        <div className="text-xs">
                          <p className="font-semibold text-slate-800">No, mantener el usuario activo</p>
                          <p className="text-red-600 text-[10px]">La acción quedará auditada. Solo recomendable para usuarios externos/proveedores con motivo claro.</p>
                        </div>
                      </label>
                    </div>
                  </CardContent>
                </Card>
              )}
              {archiveDialog.deps.has_dependencies && (
                <Card className="border-amber-200 bg-amber-50/50 rounded-sm">
                  <CardContent className="p-3">
                    <p className="text-[10px] uppercase font-bold text-amber-700 mb-1.5">Dependencias detectadas</p>
                    <div className="grid grid-cols-2 gap-1.5 text-[11px]">
                      {archiveDialog.deps.requests_count > 0 && <span className="text-slate-600">{archiveDialog.deps.requests_count} solicitudes RRHH</span>}
                      {archiveDialog.deps.documents_count > 0 && <span className="text-slate-600">{archiveDialog.deps.documents_count} documentos</span>}
                      {archiveDialog.deps.history_count > 0 && <span className="text-slate-600">{archiveDialog.deps.history_count} registros de historial</span>}
                      {archiveDialog.deps.subordinates_count > 0 && <span className="text-slate-600">{archiveDialog.deps.subordinates_count} subordinados directos</span>}
                      {archiveDialog.deps.is_approver && <span className="text-red-600 font-semibold">Es aprobador en la matriz</span>}
                    </div>
                  </CardContent>
                </Card>
              )}
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Motivo del archivado *</Label>
                <Textarea
                  value={archiveReason}
                  onChange={e => setArchiveReason(e.target.value)}
                  placeholder="Indica el motivo de la baja/archivado del empleado..."
                  className="rounded-sm border-slate-200 text-xs min-h-[60px]"
                  data-testid="archive-reason-input"
                />
              </div>
              <p className="text-[10px] text-slate-400">El empleado puede restaurarse posteriormente.</p>
            </div>
          )}
          <DialogFooter className="mt-2">
            <Button variant="outline" size="sm" className="text-xs rounded-sm" onClick={() => { setArchiveDialog(null); setArchiveReason(""); }}>Cancelar</Button>
            <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white text-xs rounded-sm" onClick={confirmArchive}
              disabled={archiving || !archiveReason.trim()} data-testid="btn-confirm-archive">
              {archiving ? "Archivando..." : "Confirmar Archivado"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
